import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { chatModel } from '../../data/chatModel';
import VideoCallUsageBadge from '../Marketplace/VideoCallUsageBadge';

const ChatWindow = ({ chatId, otherUserId, otherUserName, onClose, productInfo }) => {
  const { t } = useTranslation();
  const { sendMessage, loadMessages, subscribeToMessages, markChatAsRead, messages: contextMessages, chats } = useChat();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [product, setProduct] = useState(productInfo || null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const messages = contextMessages[chatId] || [];
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (!chatId) return;

    // Load messages
    loadMessages(chatId);

    // Subscribe to new messages
    const unsubscribe = subscribeToMessages(chatId);
    
    // Mark chat as read
    markChatAsRead(chatId);

    return () => {
      if (unsubscribe) unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  // Load product info when chats are loaded or productInfo prop changes
  useEffect(() => {
    if (productInfo) {
      setProduct(productInfo);
      return;
    }

    if (!chatId || chats.length === 0) return;

    loadProductInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, chats, productInfo]);

  const loadProductInfo = async () => {
    try {
      // Try to fetch chat directly from Firestore first
      const chatRef = doc(db, 'chats', chatId);
      const chatSnap = await getDoc(chatRef);
      
      if (chatSnap.exists()) {
        const chatData = chatSnap.data();
        console.log('Chat data:', chatData);
        
        // Check if it's a product inquiry chat
        const isProductInquiry = chatData.type === 'product_inquiry' || 
                                 chatData.type === 'PRODUCT_INQUIRY' ||
                                 chatData.type === chatModel.types.PRODUCT_INQUIRY;
        
        if (isProductInquiry && chatData.productId) {
          console.log('Loading product:', chatData.productId);
          const productRef = doc(db, 'products', chatData.productId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            const productData = { id: productSnap.id, ...productSnap.data() };
            console.log('Product loaded:', productData);
            setProduct(productData);
          } else {
            console.log('Product not found:', chatData.productId);
          }
        } else {
          console.log('Not a product inquiry chat or no productId:', {
            type: chatData.type,
            productId: chatData.productId
          });
        }
      } else {
        // Fallback: try to find in context
        const chat = chats.find(c => c.id === chatId);
        if (chat && (chat.type === 'product_inquiry' || chat.type === 'PRODUCT_INQUIRY') && chat.productId) {
          const productRef = doc(db, 'products', chat.productId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            setProduct({ id: productSnap.id, ...productSnap.data() });
          }
        }
      }
    } catch (error) {
      console.error('Error loading product info:', error);
    }
  };

  // Handle user scrolling
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      
      setIsUserScrolling(!isNearBottom);
      setShouldAutoScroll(isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto scroll only when:
  // 1. Initial load
  // 2. New message from current user
  // 3. User is already at bottom
  useEffect(() => {
    if (!chatId || messages.length === 0) return;

    if (initialLoad) {
      // Initial load - scroll to bottom
      setTimeout(() => {
        scrollToBottom(false);
        setInitialLoad(false);
      }, 100);
    } else if (shouldAutoScroll) {
      // Check if last message is from current user
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.senderId === currentUser?.uid) {
        // User sent a message - always scroll
        setTimeout(() => scrollToBottom(false), 100);
      } else if (!isUserScrolling) {
        // New message and user is at bottom - scroll
        setTimeout(() => scrollToBottom(false), 100);
      }
    }
  }, [messages, chatId, shouldAutoScroll, isUserScrolling, initialLoad, currentUser]);

  const scrollToBottom = (smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!messageText.trim() || sending) return;

    setSending(true);
    try {
      await sendMessage(chatId, messageText);
      setMessageText('');
      setShouldAutoScroll(true);
      setTimeout(() => scrollToBottom(false), 100);
    } catch (error) {
      console.error('Error sending message:', error);
      alert(t('chat.sendError'));
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'เมื่อสักครู่';
    if (minutes < 60) return `${minutes} นาทีที่แล้ว`;
    if (hours < 24) return `${hours} ชั่วโมงที่แล้ว`;
    if (days < 7) return `${days} วันที่แล้ว`;
    return date.toLocaleDateString('th-TH', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-lg" style={{ minHeight: 0 }}>
      {/* Header */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
              {otherUserName?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-800 truncate">{otherUserName || 'ผู้ใช้'}</h3>
              <p className="text-xs text-gray-500">กำลังพิมพ์...</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {chatId && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/marketplace/video-call/${chatId}`)}
                  className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors flex-shrink-0"
                  title="Video Call"
                  aria-label="Video Call"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <VideoCallUsageBadge size="sm" />
              </div>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                aria-label="ปิด"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
        
        {/* Product Info Banner (if product inquiry) */}
        {product && (
          <div 
            className="px-4 py-2 bg-green-50 border-t border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
            onClick={() => navigate(`/marketplace/products/${product.id}`)}
          >
            <div className="flex items-center gap-3">
              {product.images && product.images.length > 0 && (
                <img 
                  src={product.images[0]} 
                  alt={product.name}
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-1">กำลังคุยเกี่ยวกับสินค้า:</p>
                <p className="text-sm font-semibold text-gray-800 truncate">{product.name}</p>
                {product.price && (
                  <p className="text-xs text-gray-600">฿{product.price.toLocaleString()}</p>
                )}
              </div>
              <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4" 
        style={{ minHeight: 0, overscrollBehavior: 'contain' }}
      >
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p>ยังไม่มีข้อความ</p>
            <p className="text-sm mt-2">เริ่มต้นการสนทนาได้เลย!</p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwn = message.senderId === currentUser?.uid;
            return (
              <div
                key={message.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    isOwn
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {!isOwn && (
                    <p className="text-xs font-semibold mb-1 opacity-75">
                      {message.senderName}
                    </p>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                  <p className={`text-xs mt-1 ${isOwn ? 'text-green-100' : 'text-gray-500'}`}>
                    {formatTime(message.createdAt)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-gray-200">
        <div className="flex gap-2">
          <input
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder={t('chat.sendMessage')}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!messageText.trim() || sending}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? 'กำลังส่ง...' : 'ส่ง'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatWindow;

